import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type { DistrictMapPmtiles } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * `generatePmtiles` callable wrapper (Phase 5.3 R-33 step 3)。
 *
 * onCall 側 (functions/src/admin/generatePmtiles.ts):
 *   - input:  `{ districtId, maxZoom: 14 | 15 | 16, regions?: string[] }`
 *   - output: `DistrictMapPmtiles & { elapsedMs: number }`
 *   - role:   master のみ (`requireMaster`)
 *   - timeout: 540s (admin UI ボタン側で「処理中」UI 必要)
 *
 * UI 状態:
 *   - 'idle': ボタン待機
 *   - 'pending': callable 実行中 (~数十秒〜数分)
 *   - 'success': 完了、result を返す
 *   - 'error': エラー (HttpsError or network)
 */

export type GeneratePmtilesUiState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: DistrictMapPmtiles & { elapsedMs: number }; error: null }
  | { status: 'error'; result: null; error: Error };

export interface GeneratePmtilesInput {
  districtId: string;
  maxZoom: 14 | 15 | 16;
  /** 使用する地方コード (例 ['kanto','chubu'])。複数指定で合成。未指定は中心から自動判定。 */
  regions?: string[];
}

export interface UseGeneratePmtilesReturn {
  state: GeneratePmtilesUiState;
  run: (input: GeneratePmtilesInput) => Promise<void>;
  reset: () => void;
}

const callable = httpsCallable<GeneratePmtilesInput, DistrictMapPmtiles & { elapsedMs: number }>(
  functions,
  'generatePmtiles',
);

export function useGeneratePmtiles(): UseGeneratePmtilesReturn {
  const [state, setState] = useState<GeneratePmtilesUiState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function run(input: GeneratePmtilesInput): Promise<void> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const res = await callable(input);
      setState({ status: 'success', result: res.data, error: null });
    } catch (e) {
      setState({
        status: 'error',
        result: null,
        error: e instanceof Error ? e : new Error(String(e)),
      });
    }
  }

  function reset(): void {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, run, reset };
}
